import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useContextMenu } from 'components/common/context-menu-manager';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  AirHandlerTypeCodeType,
  ConcealedDuctLocationType,
  CondenserPlacementType,
  ContextMenuItemType,
  GridSpaceDataType,
  LevelType,
  LineSetType
} from 'data/types';
import { setConfigurations } from 'features/configurations';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { rebuildLinesetsForCondenser } from 'utils/auto-lineset-assignment';
import { buildConcealedDuctLayoutsForConfiguration } from 'utils/concealed-duct-geometry';
import {
  getRadialDuctPassiveSpaces,
  isAirHandlerAvailabilityUnknown
} from 'utils/air-handler-availability';
import { getPassiveSpaceIdsForZone } from 'utils/concealed-duct-passive-spaces';
import {
  getConcealedDuctPlacementOptions,
  resolveConcealedDuctLocation
} from 'utils/concealed-duct-placement';
import {
  calculateHeightOffGround,
  findZoneByAirHandlerId,
  markCondensersStale
} from 'utils/configurations';
import { snapPxToGrid } from 'utils/duct-runs';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import {
  isUserPositionedOnPlacement,
  snapAirHandlerIntoSpace
} from 'utils/air-handler-placement';
import {
  findLevelForSpace,
  findNearestExternalEdge,
  findNearestSpace,
  getContainingSpace,
  getGridSpacesForLevel,
  isInsideAnySpace,
  moveOutsideSpaces
} from 'utils/grid';
import { clampPointToSpacePolygon } from 'utils/point-clamp';
import { syncCondenserLinesetAnchors } from 'utils/lineset-anchors';
import { buildLinesetForPair } from 'utils/linesets';
import {
  findSpaceById,
  getLowestNonBasementLevelIndex,
  getRestrictedAirHandlerTypes
} from 'utils/structure';

export type EquipmentMenuObjectType = {
  id: string;
  itemId: string;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  type: string;
  airHandlerTypeCode?: AirHandlerTypeCodeType;
  placementType?: 'ground' | 'wall' | 'roof';
  levelUniqueId?: string;
};

type UseEquipmentContextMenuArgs = {
  object: EquipmentMenuObjectType;
  configurationId?: string;
  gridSpaces?: GridSpaceDataType[];
  structure?: LevelType[];
};

export function useEquipmentContextMenu({
  object,
  configurationId,
  gridSpaces = [],
  structure = []
}: UseEquipmentContextMenuArgs) {
  const dispatch = useAppDispatch();
  const { configurations } = useAppSelector((state) => state.configurations);
  const { structure: structureFromStore } = useAppSelector(
    (state) => state.structure
  );
  const { fetchingCondenserIds, activeLevelIndex, zoneDuctModes } =
    useAppSelector((state) => state.ui);
  const { openContextMenu, updateContextMenuItems, isMenuOpenForSource } =
    useContextMenu();
  const pendingLinesetRegenRef = useRef<string | null>(null);
  const effectiveStructure =
    structure.length > 0 ? structure : structureFromStore;

  const generateLineSet = useCallback(
    (
      condenserPos: { x: number; y: number; width: number; height: number },
      airHandlerPos: { x: number; y: number; width: number; height: number },
      condenserUniqueId: string,
      airHandlerUniqueId: string,
      targetSpaceId?: string,
      condenserLevelUniqueId?: string,
      airHandlerLevelUniqueId?: string,
      airHandlerTypeCode?: AirHandlerTypeCodeType
    ) => {
      if (!configurationId) {
        return;
      }

      const currentConfig = configurations.find(
        (c) => c.uniqueId === configurationId
      );
      const zoneForAirHandler = currentConfig
        ? findZoneByAirHandlerId(currentConfig, airHandlerUniqueId)
        : null;

      const newLineSet = buildLinesetForPair(
        {
          uniqueId: condenserUniqueId,
          levelUniqueId: condenserLevelUniqueId,
          position: { x: condenserPos.x, y: condenserPos.y }
        },
        {
          uniqueId: airHandlerUniqueId,
          levelUniqueId: airHandlerLevelUniqueId,
          position: { x: airHandlerPos.x, y: airHandlerPos.y },
          targetSpaceId,
          airHandlerTypeCode,
          concealedLocation:
            zoneForAirHandler?.zone.airHandler?.concealedLocation ?? null
        },
        effectiveStructure,
        (levelIndex) => getGridSpacesForLevel(effectiveStructure, levelIndex),
        configurationId
      );

      // A line set is not a valid-systems input: no stale mark, no refetch.
      const updatedConfigurations = produce(configurations, (draft) => {
        const config = draft.find((c) => c.uniqueId === configurationId);
        if (!config) return;
        if (!config.lineSets) {
          config.lineSets = [];
        }
        config.lineSets.push(newLineSet);
      });

      dispatch(setConfigurations(updatedConfigurations));
    },
    [configurationId, configurations, dispatch, effectiveStructure]
  );

  const deleteCondenser = useCallback(
    (condenserUniqueId: string) => {
      if (!configurationId) return;

      let receivingCondenserId: string | undefined;

      const updatedConfigurations = produce(configurations, (draft) => {
        const config = draft.find((c) => c.uniqueId === configurationId);
        if (!config) return;

        const condenserIndex = config.condensers.findIndex(
          (c) => c.uniqueId === condenserUniqueId
        );
        if (condenserIndex === -1) return;

        const condenserToDelete = config.condensers[condenserIndex];

        const remainingCondensers = config.condensers.filter(
          (_, i) => i !== condenserIndex
        );

        if (
          remainingCondensers.length > 0 &&
          condenserToDelete.zones.length > 0
        ) {
          const deletedPos = condenserToDelete.position;
          let nearestCondenser = remainingCondensers[0];

          if (deletedPos && remainingCondensers.length > 1) {
            let minDist = Infinity;
            for (const c of remainingCondensers) {
              if (c.position) {
                const dist = Math.hypot(
                  c.position.x - deletedPos.x,
                  c.position.y - deletedPos.y
                );
                if (dist < minDist) {
                  minDist = dist;
                  nearestCondenser = c;
                }
              }
            }
          }

          const target = config.condensers.find(
            (c) => c.uniqueId === nearestCondenser.uniqueId
          );
          if (target) {
            receivingCondenserId = target.uniqueId;
            for (const zone of condenserToDelete.zones) {
              target.zones.push({ ...zone });
            }
          }
        }

        config.condensers.splice(condenserIndex, 1);

        if (config.lineSets) {
          config.lineSets = config.lineSets.filter(
            (ls) => ls.condenserUniqueId !== condenserUniqueId
          );
        }
      });

      const finalConfigurations = updatedConfigurations.map((config) =>
        config.uniqueId === configurationId
          ? markCondensersStale(
              config,
              receivingCondenserId ? [receivingCondenserId] : []
            )
          : config
      );

      dispatch(setConfigurations(finalConfigurations));
    },
    [configurationId, configurations, dispatch]
  );

  const deleteAirHandler = useCallback(
    (airHandlerId: string) => {
      if (!configurationId) return;

      const zoneItemId = airHandlerId.replace('-air-handler', '');

      let affectedCondenserId: string | undefined;

      let updatedConfigurations = produce(configurations, (draft) => {
        const config = draft.find((c) => c.uniqueId === configurationId);
        if (!config) return;

        let actualAirHandlerUniqueId: string | undefined;
        let deletedTypeCode: AirHandlerTypeCodeType | undefined;
        for (const condenser of config.condensers) {
          for (const zone of condenser.zones) {
            if (zone.zoneItems.some((zi) => zi.uniqueId === zoneItemId)) {
              actualAirHandlerUniqueId = zone.airHandler?.uniqueId;
              deletedTypeCode = zone.airHandler?.airHandlerTypeCode;
              affectedCondenserId = condenser.uniqueId;
              break;
            }
          }
          if (actualAirHandlerUniqueId) break;
        }

        if (config.lineSets && actualAirHandlerUniqueId) {
          config.lineSets = config.lineSets.filter(
            (ls) => ls.airHandlerUniqueId !== actualAirHandlerUniqueId
          );
        }

        for (const condenser of config.condensers) {
          const zoneIndex = condenser.zones.findIndex((zone) =>
            zone.zoneItems.some((zi) => zi.uniqueId === zoneItemId)
          );
          if (zoneIndex !== -1) {
            const zone = condenser.zones[zoneIndex];
            delete zone.airHandler;
            if (
              deletedTypeCode === 'concealed' ||
              deletedTypeCode === 'concealed_high_static'
            ) {
              delete zone.returnOverride;
              delete zone.additionalReturnRuns;
              delete zone.returnRunPoints;
              zone.zoneItems.forEach((zi) => {
                /* eslint-disable no-param-reassign */
                delete zi.registerOverride;
                delete zi.additionalSupplyRuns;
                delete zi.supplyRunPoints;
                /* eslint-enable no-param-reassign */
              });
            }
            break;
          }
        }
      });

      updatedConfigurations = updatedConfigurations.map((config) =>
        config.uniqueId === configurationId
          ? markCondensersStale(
              config,
              affectedCondenserId ? [affectedCondenserId] : []
            )
          : config
      );

      dispatch(setConfigurations(updatedConfigurations));
    },
    [configurationId, configurations, dispatch]
  );

  const getContextMenuItems = useCallback((): ContextMenuItemType[] => {
    if (
      !configurationId ||
      (object.type !== 'condenser' && object.type !== 'air-handler')
    ) {
      return [];
    }

    const config = configurations.find((c) => c.uniqueId === configurationId);
    if (!config) return [];

    const menuItems: ContextMenuItemType[] = [];

    if (object.type === 'condenser') {
      const currentCondenser = config.condensers.find(
        (c) => c.uniqueId === object.itemId
      );
      const condenserLevelUniqueId = currentCondenser?.levelUniqueId;

      const connectedAirHandlerIds = new Set(
        config.lineSets?.map((ls) => ls.airHandlerUniqueId) || []
      );

      const linesetAirHandlers: Array<{
        airHandlerUniqueId: string;
        name: string;
        position: { x: number; y: number; width: number; height: number };
        spaceId?: string;
        levelUniqueId?: string;
        isCrossFloor: boolean;
        airHandlerTypeCode?: AirHandlerTypeCodeType;
      }> = [];

      config.condensers.forEach((condenser) => {
        condenser.zones.forEach((zone) => {
          const { zoneItems, airHandler } = zone;

          if (zoneItems.length > 0 && airHandler) {
            if (connectedAirHandlerIds.has(airHandler.uniqueId)) {
              return;
            }

            const { structureItemId } = zoneItems[0];
            const airHandlerLevel = findLevelForSpace(
              structureItemId,
              effectiveStructure
            );
            const airHandlerLevelUniqueId = airHandlerLevel?.uniqueId;
            const isCrossFloor =
              !!condenserLevelUniqueId &&
              !!airHandlerLevelUniqueId &&
              condenserLevelUniqueId !== airHandlerLevelUniqueId;
            const levelLabel = airHandlerLevel?.label || '';
            const spaceNames = zoneItems
              .map((item) => {
                const space = findSpaceById(
                  effectiveStructure,
                  item.structureItemId
                );
                return space?.label || item.name;
              })
              .join(', ');
            const zoneLabel = zone.name || zoneItems[0].name;
            const displayName = isCrossFloor
              ? `${zoneLabel} (${spaceNames}) - ${levelLabel}`
              : `${zoneLabel} (${spaceNames})`;

            linesetAirHandlers.push({
              airHandlerUniqueId: airHandler.uniqueId,
              name: displayName,
              position: {
                x: airHandler.positionX,
                y: airHandler.positionY,
                width: 4,
                height: 4
              },
              spaceId: structureItemId,
              levelUniqueId: airHandlerLevelUniqueId,
              isCrossFloor,
              airHandlerTypeCode: airHandler.airHandlerTypeCode
            });
          }
        });
      });

      if (linesetAirHandlers.length > 0) {
        menuItems.push({
          label: 'Generate Line Set',
          submenu: linesetAirHandlers.map((ah) => ({
            label: ah.name,
            onClick: () => {
              generateLineSet(
                object.position,
                ah.position,
                object.itemId,
                ah.airHandlerUniqueId,
                ah.spaceId,
                condenserLevelUniqueId,
                ah.levelUniqueId,
                ah.airHandlerTypeCode
              );
            }
          }))
        });

        menuItems.push({
          separator: true
        });
      }

      const gridSquares = 3;
      const objectSize = gridSquares * STRUCTURE_GRID_SIZE;

      const setPlacementType = (value: CondenserPlacementType) => {
        let levelChanged = false;
        const updated = produce(configurations, (draft) => {
          const cfg = draft.find((c) => c.uniqueId === configurationId);
          if (!cfg) return;
          const cond = cfg.condensers.find((c) => c.uniqueId === object.itemId);
          if (!cond || !cond.position) return;

          const previousLevelUniqueId = cond.levelUniqueId;
          cond.placementType = value;

          let targetGridSpaces = gridSpaces;

          if (value === 'ground') {
            const lowestIndex =
              getLowestNonBasementLevelIndex(effectiveStructure);
            const lowestLevel = effectiveStructure[lowestIndex];
            if (lowestLevel && cond.levelUniqueId !== lowestLevel.uniqueId) {
              cond.levelUniqueId = lowestLevel.uniqueId;
              targetGridSpaces = getGridSpacesForLevel(
                effectiveStructure,
                lowestIndex
              );
            }
          } else if (value === 'roof') {
            const topIndex = effectiveStructure.length - 1;
            const topLevel = effectiveStructure[topIndex];
            if (topLevel && cond.levelUniqueId !== topLevel.uniqueId) {
              cond.levelUniqueId = topLevel.uniqueId;
              targetGridSpaces = getGridSpacesForLevel(
                effectiveStructure,
                topIndex
              );
            }
          }

          let { x, y } = cond.position;

          if (value === 'ground') {
            if (isInsideAnySpace({ x, y }, objectSize, targetGridSpaces)) {
              const newPos = moveOutsideSpaces(
                { x, y },
                objectSize,
                targetGridSpaces
              );
              x = newPos.x;
              y = newPos.y;
            }
          } else if (value === 'wall') {
            const edge = findNearestExternalEdge(
              { x, y },
              objectSize,
              targetGridSpaces,
              getEffectiveFootprintPolygons(
                effectiveStructure.find(
                  (level) => level.uniqueId === cond.levelUniqueId
                )
              )
            );
            if (edge) {
              x = edge.x;
              y = edge.y;
            }
          } else if (value === 'roof') {
            if (!isInsideAnySpace({ x, y }, objectSize, targetGridSpaces)) {
              const nearest = findNearestSpace(
                { x, y },
                objectSize,
                targetGridSpaces
              );
              if (nearest) {
                x = nearest.position.x / STRUCTURE_GRID_SIZE;
                y = nearest.position.y / STRUCTURE_GRID_SIZE;
              }
            }
          }

          cond.position = { x, y };
          cond.heightOffGround = calculateHeightOffGround(
            value,
            cond.levelUniqueId,
            effectiveStructure
          );

          levelChanged = cond.levelUniqueId !== previousLevelUniqueId;
          if (levelChanged) {
            const getGridSpacesForLevelByIndex = (levelIndex: number) =>
              getGridSpacesForLevel(effectiveStructure, levelIndex);
            cfg.lineSets = rebuildLinesetsForCondenser(
              cfg,
              object.itemId,
              effectiveStructure,
              getGridSpacesForLevelByIndex,
              buildConcealedDuctLayoutsForConfiguration(
                cfg,
                effectiveStructure,
                getGridSpacesForLevelByIndex,
                zoneDuctModes
              )
            );
          } else {
            syncCondenserLinesetAnchors(
              cfg,
              object.itemId,
              { x: x + gridSquares / 2, y: y + gridSquares / 2 },
              cond.levelUniqueId
            );
          }
        });
        const finalConfigurations = levelChanged
          ? updated.map((cfg) =>
              cfg.uniqueId === configurationId
                ? markCondensersStale(cfg, [object.itemId])
                : cfg
            )
          : updated;
        dispatch(setConfigurations(finalConfigurations));
      };

      menuItems.push({
        label: 'Location',
        submenu: [
          {
            label: 'Ground',
            checked: currentCondenser?.placementType === 'ground',
            onClick: () => setPlacementType('ground')
          },
          {
            label: 'Wall',
            checked: currentCondenser?.placementType === 'wall',
            onClick: () => setPlacementType('wall')
          },
          {
            label: 'Roof',
            checked: currentCondenser?.placementType === 'roof',
            onClick: () => setPlacementType('roof')
          }
        ]
      });

      const moveToLevel = (targetLevel: LevelType) => {
        const targetIndex = effectiveStructure.findIndex(
          (l) => l.uniqueId === targetLevel.uniqueId
        );
        if (targetIndex === -1) return;
        const groundIndex = getLowestNonBasementLevelIndex(effectiveStructure);

        const updated = produce(configurations, (draft) => {
          const cfg = draft.find((c) => c.uniqueId === configurationId);
          if (!cfg) return;
          const cond = cfg.condensers.find((c) => c.uniqueId === object.itemId);
          if (!cond || !cond.position) return;

          const placement: CondenserPlacementType =
            targetIndex <= groundIndex && cond.placementType === 'ground'
              ? 'ground'
              : 'wall';

          cond.levelUniqueId = targetLevel.uniqueId;
          cond.placementType = placement;

          const targetGridSpaces = getGridSpacesForLevel(
            effectiveStructure,
            targetIndex
          );

          let { x, y } = cond.position;
          if (placement === 'wall') {
            const edge = findNearestExternalEdge(
              { x, y },
              objectSize,
              targetGridSpaces,
              getEffectiveFootprintPolygons(targetLevel)
            );
            if (edge) {
              x = edge.x;
              y = edge.y;
            }
          } else if (isInsideAnySpace({ x, y }, objectSize, targetGridSpaces)) {
            const newPos = moveOutsideSpaces(
              { x, y },
              objectSize,
              targetGridSpaces
            );
            x = newPos.x;
            y = newPos.y;
          }

          cond.position = { x, y };
          cond.modified = true;
          cond.heightOffGround = calculateHeightOffGround(
            placement,
            targetLevel.uniqueId,
            effectiveStructure
          );

          const getGridSpacesForLevelByIndex = (levelIndex: number) =>
            getGridSpacesForLevel(effectiveStructure, levelIndex);
          cfg.lineSets = rebuildLinesetsForCondenser(
            cfg,
            object.itemId,
            effectiveStructure,
            getGridSpacesForLevelByIndex,
            buildConcealedDuctLayoutsForConfiguration(
              cfg,
              effectiveStructure,
              getGridSpacesForLevelByIndex,
              zoneDuctModes
            )
          );
          cfg.modified = true;
        });

        const finalConfigurations = updated.map((cfg) =>
          cfg.uniqueId === configurationId
            ? markCondensersStale(cfg, [object.itemId])
            : cfg
        );
        dispatch(setConfigurations(finalConfigurations));
      };

      const moveToLevels = effectiveStructure.filter(
        (level) =>
          level.type === 'level' &&
          level.uniqueId !== currentCondenser?.levelUniqueId
      );

      if (moveToLevels.length > 0) {
        menuItems.push({
          label: 'Move to',
          submenu: moveToLevels.map((level) => ({
            label: level.label,
            onClick: () => moveToLevel(level)
          }))
        });
      }

      menuItems.push({ separator: true });

      menuItems.push({
        label: 'Delete Condenser',
        onClick: () => {
          deleteCondenser(object.itemId);
        }
      });
    } else if (object.type === 'air-handler') {
      const airHandlerZoneItemId = object.itemId;

      let airHandlerLevelUniqueId: string | undefined;
      let airHandlerStructureItemId: string | undefined;
      let airHandlerUniqueId: string | undefined;
      let airHandlerCondenserId: string | undefined;
      let currentAirHandlerZone:
        | (typeof config.condensers)[0]['zones'][0]
        | undefined;

      for (const condenser of config.condensers) {
        for (const zone of condenser.zones) {
          if (zone.airHandler?.uniqueId === airHandlerZoneItemId) {
            airHandlerUniqueId = zone.airHandler.uniqueId;
            currentAirHandlerZone = zone;
            airHandlerCondenserId = condenser.uniqueId;
            const firstZoneItem = zone.zoneItems[0];
            if (firstZoneItem) {
              airHandlerStructureItemId = firstZoneItem.structureItemId;
            }
            break;
          }
        }
        if (currentAirHandlerZone) break;
      }

      // Availability comes from the options pool during the air-handler step
      // (the full rows are gated), so "refreshing" is an unknown pool or an
      // in-flight request — not the full-fetch flag.
      const condenserIsRefreshing =
        airHandlerCondenserId !== undefined &&
        (fetchingCondenserIds.includes(airHandlerCondenserId) ||
          config.condensers.some(
            (c) =>
              c.uniqueId === airHandlerCondenserId &&
              isAirHandlerAvailabilityUnknown(c)
          ));

      const airHandlerHasLineset =
        airHandlerUniqueId &&
        config.lineSets?.some(
          (ls) => ls.airHandlerUniqueId === airHandlerUniqueId
        );

      if (airHandlerStructureItemId) {
        const level = findLevelForSpace(
          airHandlerStructureItemId,
          effectiveStructure
        );
        airHandlerLevelUniqueId = level?.uniqueId;
      }

      if (!airHandlerHasLineset) {
        const availableCondensers: Array<{
          id: string;
          name: string;
          position: { x: number; y: number; width: number; height: number };
          levelUniqueId?: string;
          isCrossFloor: boolean;
        }> = [];

        config.condensers.forEach((condenser) => {
          if (condenser.position) {
            const isCrossFloor =
              !!airHandlerLevelUniqueId &&
              !!condenser.levelUniqueId &&
              airHandlerLevelUniqueId !== condenser.levelUniqueId;
            const condenserLevel = effectiveStructure.find(
              (l) => l.uniqueId === condenser.levelUniqueId
            );
            const levelLabel = condenserLevel?.label || '';
            const displayName = isCrossFloor
              ? `${condenser.name} (${levelLabel})`
              : condenser.name;

            availableCondensers.push({
              id: condenser.uniqueId,
              name: displayName,
              position: {
                x: condenser.position.x,
                y: condenser.position.y,
                width: 4,
                height: 4
              },
              levelUniqueId: condenser.levelUniqueId,
              isCrossFloor
            });
          }
        });

        if (availableCondensers.length > 0) {
          menuItems.push({
            label: 'Generate Line Set',
            submenu: availableCondensers.map((condenser) => ({
              label: condenser.name,
              onClick: () => {
                generateLineSet(
                  condenser.position,
                  object.position,
                  condenser.id,
                  airHandlerZoneItemId,
                  undefined,
                  condenser.levelUniqueId,
                  airHandlerLevelUniqueId,
                  object.airHandlerTypeCode
                );
              }
            }))
          });

          menuItems.push({
            separator: true
          });
        }
      }

      if (currentAirHandlerZone?.airHandler) {
        const currentTypeCode =
          currentAirHandlerZone.airHandler.airHandlerTypeCode;
        const restrictedTypes = getRestrictedAirHandlerTypes(
          currentAirHandlerZone,
          effectiveStructure
        );

        const setAirHandlerType = (newTypeCode: AirHandlerTypeCodeType) => {
          const resolvedTypeCode = newTypeCode;

          const existingLineset = config.lineSets?.find(
            (ls) => ls.airHandlerUniqueId === airHandlerUniqueId
          );
          if (existingLineset) {
            pendingLinesetRegenRef.current = existingLineset.condenserUniqueId;
          }

          let updated = produce(configurations, (draft) => {
            const cfg = draft.find((c) => c.uniqueId === configurationId);
            if (!cfg) return;
            for (const cond of cfg.condensers) {
              for (const zone of cond.zones) {
                if (
                  zone.airHandler &&
                  zone.airHandler.uniqueId === airHandlerUniqueId
                ) {
                  zone.airHandler.airHandlerTypeCode = resolvedTypeCode;
                  delete (zone as any).selectedProduct;
                  break;
                }
              }
            }
            if (existingLineset && cfg.lineSets) {
              cfg.lineSets = cfg.lineSets.filter(
                (ls) => ls.airHandlerUniqueId !== airHandlerUniqueId
              );
            }
          });
          if (airHandlerCondenserId) {
            const condenserId = airHandlerCondenserId;
            updated = updated.map((cfg) =>
              cfg.uniqueId === configurationId
                ? markCondensersStale(cfg, [condenserId])
                : cfg
            );
          }
          dispatch(setConfigurations(updated));
        };

        const typeMenuItems: {
          label: string;
          code: AirHandlerTypeCodeType;
        }[] = [
          { label: 'High Wall', code: 'wall' },
          { label: 'Low Wall', code: 'heatingConsole' },
          { label: 'Floor Ceiling Console', code: 'console' },
          { label: 'Ceiling Cassette', code: 'cassette' },
          { label: 'Ceiling Cassette Slim Fit', code: 'cassette_16' },
          { label: 'Concealed Duct', code: 'concealed' }
        ];

        menuItems.push({
          label: 'Type',
          submenu: typeMenuItems.map((item) => {
            const isStructurallyRestricted = restrictedTypes.includes(
              item.code
            );
            return {
              label: item.label,
              checked: currentTypeCode === item.code,
              disabled: isStructurallyRestricted || condenserIsRefreshing,
              onClick: () => setAirHandlerType(item.code)
            };
          })
        });

        menuItems.push({ separator: true });
      }

      const isConcealedAirHandler =
        currentAirHandlerZone?.airHandler?.airHandlerTypeCode === 'concealed' ||
        currentAirHandlerZone?.airHandler?.airHandlerTypeCode ===
          'concealed_high_static';

      if (isConcealedAirHandler && currentAirHandlerZone) {
        const placementLevelUniqueId =
          object.levelUniqueId ?? airHandlerLevelUniqueId;
        const placementLevelIndex = placementLevelUniqueId
          ? effectiveStructure.findIndex(
              (l) => l.uniqueId === placementLevelUniqueId
            )
          : -1;
        const placementOptions = getConcealedDuctPlacementOptions(
          placementLevelIndex,
          effectiveStructure
        );

        if (placementOptions.attic && placementOptions.basement) {
          const currentLocation = resolveConcealedDuctLocation(
            currentAirHandlerZone.airHandler?.concealedLocation,
            placementLevelIndex,
            effectiveStructure
          );

          // The concealed location only steers the line-set route; it is not
          // a valid-systems input, so no condenser is marked stale.
          const setConcealedLocation = (
            location: ConcealedDuctLocationType
          ) => {
            const updated = produce(configurations, (draft) => {
              const cfg = draft.find((c) => c.uniqueId === configurationId);
              if (!cfg || !airHandlerUniqueId) return;
              const zoneMatch = findZoneByAirHandlerId(cfg, airHandlerUniqueId);
              if (!zoneMatch?.zone.airHandler) return;
              zoneMatch.zone.airHandler.concealedLocation = location;
              zoneMatch.zone.airHandler.modified = true;

              const owningLineset = cfg.lineSets?.find(
                (ls) => ls.airHandlerUniqueId === airHandlerUniqueId
              );
              if (!owningLineset) return;
              const getGridSpacesForLevelByIndex = (levelIndex: number) =>
                getGridSpacesForLevel(effectiveStructure, levelIndex);
              cfg.lineSets = rebuildLinesetsForCondenser(
                cfg,
                owningLineset.condenserUniqueId,
                effectiveStructure,
                getGridSpacesForLevelByIndex,
                buildConcealedDuctLayoutsForConfiguration(
                  cfg,
                  effectiveStructure,
                  getGridSpacesForLevelByIndex,
                  zoneDuctModes
                )
              );
            });
            dispatch(setConfigurations(updated));
          };

          menuItems.push({
            label: 'Location',
            submenu: [
              {
                label: 'Attic',
                checked: currentLocation === 'attic',
                onClick: () => setConcealedLocation('attic')
              },
              {
                label: 'Basement',
                checked: currentLocation === 'basement',
                onClick: () => setConcealedLocation('basement')
              }
            ]
          });
        }

        const zoneUniqueId = currentAirHandlerZone.uniqueId;
        const owningCondenser = configurations
          .find((c) => c.uniqueId === configurationId)
          ?.condensers.find((c) => c.uniqueId === airHandlerCondenserId);
        const passiveSpaceIds = getPassiveSpaceIdsForZone(
          currentAirHandlerZone,
          owningCondenser?.validSystems ?? [],
          zoneDuctModes[zoneUniqueId],
          getRadialDuctPassiveSpaces(owningCondenser?.airHandlerOptions)
        );
        const zoneItemsForMenu = currentAirHandlerZone.zoneItems.filter(
          (zi) => !passiveSpaceIds.has(zi.structureItemId)
        );

        const computeDefaultSupplyPx = (
          structureItemId: string
        ): { x: number; y: number } => {
          const gs = gridSpaces.find((g) => g.spaceId === structureItemId);
          if (!gs) return { x: 0, y: 0 };
          const bbCenter = {
            x: gs.position.x + gs.position.width / 2,
            y: gs.position.y + gs.position.height / 2
          };
          return clampPointToSpacePolygon(snapPxToGrid(bbCenter), gs);
        };

        const addSupplyRun = (structureItemId: string) => {
          const defaultPx = computeDefaultSupplyPx(structureItemId);
          let updated = produce(configurations, (draft) => {
            const cfg = draft.find((c) => c.uniqueId === configurationId);
            if (!cfg) return;
            cfg.condensers.forEach((cond) => {
              cond.zones.forEach((zone) => {
                if (zone.uniqueId !== zoneUniqueId) return;
                const zoneItem = zone.zoneItems.find(
                  (zi) => zi.structureItemId === structureItemId
                );
                if (!zoneItem) return;
                const existing = zoneItem.additionalSupplyRuns ?? [];
                zoneItem.additionalSupplyRuns = [
                  ...existing,
                  {
                    uniqueId: nanoid(7),
                    x: defaultPx.x,
                    y: defaultPx.y
                  }
                ];
              });
            });
          });
          if (airHandlerCondenserId) {
            const condenserId = airHandlerCondenserId;
            updated = updated.map((cfg) =>
              cfg.uniqueId === configurationId
                ? markCondensersStale(cfg, [condenserId])
                : cfg
            );
          }
          dispatch(setConfigurations(updated));
        };

        const addReturnRun = () => {
          let bestSpaceId: string | null = null;
          let bestArea = -Infinity;
          zoneItemsForMenu.forEach((zi) => {
            const gs = gridSpaces.find((g) => g.spaceId === zi.structureItemId);
            if (!gs) return;
            const area = gs.position.width * gs.position.height;
            if (area > bestArea) {
              bestArea = area;
              bestSpaceId = zi.structureItemId;
            }
          });
          const defaultPx = bestSpaceId
            ? computeDefaultSupplyPx(bestSpaceId)
            : snapPxToGrid({
                x:
                  Math.round(object.position.x) * STRUCTURE_GRID_SIZE +
                  (3 * STRUCTURE_GRID_SIZE) / 2,
                y:
                  Math.round(object.position.y) * STRUCTURE_GRID_SIZE +
                  (3 * STRUCTURE_GRID_SIZE) / 2
              });

          let updated = produce(configurations, (draft) => {
            const cfg = draft.find((c) => c.uniqueId === configurationId);
            if (!cfg) return;
            cfg.condensers.forEach((cond) => {
              cond.zones.forEach((zone) => {
                if (zone.uniqueId !== zoneUniqueId) return;
                const existing = zone.additionalReturnRuns ?? [];
                // eslint-disable-next-line no-param-reassign
                zone.additionalReturnRuns = [
                  ...existing,
                  {
                    uniqueId: nanoid(7),
                    x: defaultPx.x,
                    y: defaultPx.y
                  }
                ];
              });
            });
          });
          if (airHandlerCondenserId) {
            const condenserId = airHandlerCondenserId;
            updated = updated.map((cfg) =>
              cfg.uniqueId === configurationId
                ? markCondensersStale(cfg, [condenserId])
                : cfg
            );
          }
          dispatch(setConfigurations(updated));
        };

        if (zoneItemsForMenu.length > 0) {
          menuItems.push({
            label: 'Add Supply Run',
            submenu: zoneItemsForMenu.map((zi) => {
              const gs = gridSpaces.find(
                (g) => g.spaceId === zi.structureItemId
              );
              return {
                label: gs?.label ?? zi.name ?? 'Room',
                disabled: condenserIsRefreshing,
                onClick: () => addSupplyRun(zi.structureItemId)
              };
            })
          });
        }
        menuItems.push({
          label: 'Add Return Run',
          disabled: condenserIsRefreshing,
          onClick: addReturnRun
        });

        menuItems.push({ separator: true });
      }

      menuItems.push({
        label: 'Delete Air Handler',
        onClick: () => {
          deleteAirHandler(object.id);
        }
      });
    }

    return menuItems;
  }, [
    object,
    configurationId,
    configurations,
    generateLineSet,
    deleteCondenser,
    deleteAirHandler,
    effectiveStructure,
    fetchingCondenserIds,
    gridSpaces,
    zoneDuctModes,
    dispatch
  ]);

  const openEquipmentContextMenu = useCallback(
    (clientX: number, clientY: number) => {
      if (object.type !== 'condenser' && object.type !== 'air-handler') {
        return;
      }

      const menuItems = getContextMenuItems();
      if (menuItems.length === 0) return;

      openContextMenu(clientX, clientY, menuItems, object.id);
    },
    [object, getContextMenuItems, openContextMenu]
  );

  useEffect(() => {
    if (
      (object.type === 'air-handler' || object.type === 'condenser') &&
      isMenuOpenForSource(object.id)
    ) {
      updateContextMenuItems(getContextMenuItems());
    }
  }, [
    object.type,
    object.id,
    configurations,
    isMenuOpenForSource,
    getContextMenuItems,
    updateContextMenuItems
  ]);

  const typeChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTypeCodeRef = useRef(object.airHandlerTypeCode);

  useEffect(() => {
    if (
      object.type !== 'air-handler' ||
      !object.airHandlerTypeCode ||
      !configurationId ||
      object.airHandlerTypeCode === prevTypeCodeRef.current
    ) {
      prevTypeCodeRef.current = object.airHandlerTypeCode;
      return undefined;
    }
    prevTypeCodeRef.current = object.airHandlerTypeCode;

    if (typeChangeTimerRef.current) {
      clearTimeout(typeChangeTimerRef.current);
    }

    const newTypeCode = object.airHandlerTypeCode;
    typeChangeTimerRef.current = setTimeout(() => {
      const getAirHandlerLevelIndex = (): number => {
        const cfg = configurations.find((c) => c.uniqueId === configurationId);
        if (!cfg) return -1;
        for (const cond of cfg.condensers) {
          for (const zone of cond.zones) {
            if (zone.airHandler?.uniqueId === object.itemId) {
              const firstItem = zone.zoneItems[0];
              if (!firstItem) return -1;
              const level = findLevelForSpace(
                firstItem.structureItemId,
                effectiveStructure
              );
              if (!level) return -1;
              return effectiveStructure.findIndex(
                (l) => l.uniqueId === level.uniqueId
              );
            }
          }
        }
        return -1;
      };

      const airHandlerLevelIdx = getAirHandlerLevelIndex();
      const targetLevelIndex =
        airHandlerLevelIdx >= 0 ? airHandlerLevelIdx : activeLevelIndex;

      const objectPixelSize = 3 * STRUCTURE_GRID_SIZE;
      const levelGridSpaces = getGridSpacesForLevel(
        effectiveStructure,
        targetLevelIndex
      );
      const space =
        getContainingSpace(object.position, objectPixelSize, levelGridSpaces) ??
        findNearestSpace(object.position, objectPixelSize, levelGridSpaces);
      if (!space) return;

      const newPos = snapAirHandlerIntoSpace({
        airHandlerTypeCode: newTypeCode,
        position: object.position,
        objectSize: objectPixelSize,
        space,
        wallSnapOptions: {
          allGridSpaces: levelGridSpaces,
          preferExteriorEdges: true,
          footprints: getEffectiveFootprintPolygons(
            effectiveStructure[targetLevelIndex]
          )
        }
      });

      let newLineSet: LineSetType | null = null;
      const condenserUniqueId = pendingLinesetRegenRef.current;
      if (condenserUniqueId) {
        pendingLinesetRegenRef.current = null;
        const cfg = configurations.find((c) => c.uniqueId === configurationId);
        if (cfg) {
          const condenser = cfg.condensers.find(
            (c) => c.uniqueId === condenserUniqueId
          );
          if (condenser?.position) {
            const zoneForAirHandler = findZoneByAirHandlerId(
              cfg,
              object.itemId
            );
            const firstItem = zoneForAirHandler?.zone.zoneItems[0];
            const ahLevelUniqueId = firstItem
              ? findLevelForSpace(firstItem.structureItemId, effectiveStructure)
                  ?.uniqueId
              : undefined;

            const builtLineSet = buildLinesetForPair(
              {
                uniqueId: condenserUniqueId,
                levelUniqueId: condenser.levelUniqueId,
                position: {
                  x: condenser.position.x,
                  y: condenser.position.y
                }
              },
              {
                uniqueId: object.itemId,
                levelUniqueId: ahLevelUniqueId,
                position: { x: newPos.x, y: newPos.y },
                targetSpaceId: undefined,
                airHandlerTypeCode: newTypeCode,
                concealedLocation:
                  zoneForAirHandler?.zone.airHandler?.concealedLocation ?? null
              },
              effectiveStructure,
              (levelIndex) =>
                getGridSpacesForLevel(effectiveStructure, levelIndex),
              configurationId
            );

            newLineSet = builtLineSet;
          }
        }
      }

      // The type change itself already changed the request (selected type)
      // and refetched; this deferred snap only repositions the head for its
      // new footprint and regenerates its line set, neither of which the
      // valid-systems request reads. No stale mark, no second refetch.
      const updated = produce(configurations, (draft) => {
        const cfg = draft.find((c) => c.uniqueId === configurationId);
        if (!cfg) return;

        for (const cond of cfg.condensers) {
          let found = false;
          for (const zone of cond.zones) {
            if (zone.airHandler?.uniqueId === object.itemId) {
              zone.airHandler.positionX = newPos.x;
              zone.airHandler.positionY = newPos.y;
              zone.airHandler.userPositioned =
                isUserPositionedOnPlacement(newTypeCode);
              found = true;
              break;
            }
          }
          if (found) break;
        }

        if (newLineSet) {
          if (!cfg.lineSets) {
            cfg.lineSets = [];
          }
          cfg.lineSets.push(newLineSet);
        }
      });

      dispatch(setConfigurations(updated));
    }, 1000);

    return () => {
      if (typeChangeTimerRef.current) {
        clearTimeout(typeChangeTimerRef.current);
      }
    };
  }, [object.airHandlerTypeCode]);

  return useMemo(
    () => ({ getContextMenuItems, openEquipmentContextMenu }),
    [getContextMenuItems, openEquipmentContextMenu]
  );
}
